import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateStaff, useRoles, useUpdateStaff } from "@/hooks/useRbac";
import type { StaffMember } from "@/rbac/types";

interface Props {
  staff: StaffMember | null;
  onClose: () => void;
}

export const StaffEditDialog = ({ staff, onClose }: Props) => {
  const isEdit = !!staff;
  const { data: roles = [] } = useRoles();
  const createMut = useCreateStaff();
  const updateMut = useUpdateStaff();

  const [name, setName] = useState(staff?.name ?? "");
  const [email, setEmail] = useState(staff?.email ?? "");
  const [phone, setPhone] = useState(staff?.phone ?? "");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState(staff?.role?.id ?? "");

  const handleSubmit = () => {
    if (!roleId) {
      toast.error("Pick a role");
      return;
    }

    if (isEdit) {
      const patch: Parameters<typeof updateMut.mutate>[0]["data"] = {
        name,
        roleId,
      };
      if (password) patch.password = password;
      updateMut.mutate(
        { id: staff!.id, data: patch },
        {
          onSuccess: () => {
            toast.success("Staff updated");
            onClose();
          },
          onError: (e) => toast.error(e.message),
        },
      );
      return;
    }

    if (!name || !email || !phone || !password) {
      toast.error("Fill all fields");
      return;
    }
    createMut.mutate(
      { name, email, phone, password, roleId },
      {
        onSuccess: () => {
          toast.success("Staff created");
          onClose();
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit staff" : "Add staff"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update staff details or reassign their role."
              : "Invite a teammate and assign them a role."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
              type="email"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={isEdit}
            />
          </div>
          <div>
            <Label htmlFor="password">
              {isEdit ? "New password (optional)" : "Password"}
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMut.isPending || updateMut.isPending}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
